import React from "react";
import { Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";

import { FormGroup, showSnackbar } from "@cofano/cck";

import { FormDialog } from "components/dialogs/Dialogs";
import { FormErrorMessage } from "components/form/FormErrorMessage";

import SailingScheduleSelect from "view/sailingschedules/components/selects/SailingScheduleSelect";
import { updateVoyageScheduleSchema, UpdateVoyageScheduleSchemaInput, UpdateVoyageScheduleSchemaOutput } from "view/voyages/utils/voyage.schema";

import { BatchVoyageDocument } from "__generated__/view/voyages/graphql/voyage.mutations.generated";

const VoyageUpdateScheduleDialog = ({ open, onClose, voyageIds, refetch }: {
	open: boolean,
	onClose: () => void,
	voyageIds: number[];
	refetch: () => void,
}) => {

	const { control, handleSubmit, reset } = useForm<UpdateVoyageScheduleSchemaInput, unknown, UpdateVoyageScheduleSchemaOutput>({
		resolver: zodResolver(updateVoyageScheduleSchema),
		defaultValues: {
			schedule: null,
		},
	});

	const handleOnClose = () => {
		onClose();
		reset({
			schedule: null,
		});
	};

	const [ batchVoyage, { loading } ] = useMutation(BatchVoyageDocument);

	const save = (data: UpdateVoyageScheduleSchemaOutput) => {
		batchVoyage({
			variables: {
				input: {
					schedule: data.schedule,
				},
				voyages: voyageIds,
			},
		}).then(() => {
			showSnackbar(intl.formatMessage({ id: "SUCCESSFUL_SAVED" }), { variant: "success" });
			refetch();
			handleOnClose();
		});
	};

	return (
		<FormDialog
			open={open}
			onClose={handleOnClose}
			title={intl.formatMessage({ id: "CHANGE_VOYAGE_SCHEDULE" })}
			form='updateVoyageScheduleForm'
			loading={loading}>
			<form onSubmit={(event) => void handleSubmit(save)(event)} id="updateVoyageScheduleForm">
				<FormGroup label={intl.formatMessage({ id: "VOYAGE_SCHEDULE" })} overflow="hidden" required>
					<Controller
						name="schedule"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<SailingScheduleSelect { ...field } />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			</form>
		</FormDialog>
	);
};

export default VoyageUpdateScheduleDialog;
